import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMyOverview } from '../api';
import { qk } from '../api/queryKeys';
import { workspacePath } from '../lib/workspacePath';
import { LaunchMissionDialog } from './LaunchMissionDialog';

export interface LaunchMissionContextValue {
  openLaunch: () => void;
}

/**
 * Opens the app's one Launch mission dialog (the jump palette's item, the home drawer's
 * New issue). Without a provider it renders fine but says so, rather than a silent dead button.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const LaunchMissionContext = createContext<LaunchMissionContextValue>({
  openLaunch: () => console.warn('useLaunchMission: no LaunchMissionProvider above this component; the Launch mission dialog cannot open.'),
});

// eslint-disable-next-line react-refresh/only-export-components
export function useLaunchMission(): LaunchMissionContextValue {
  return useContext(LaunchMissionContext);
}

/**
 * Layout mounts this around the header and the page: it owns the one dialog. A launch
 * takes you to the new issue (there is no page-local status line to report to).
 */
export function LaunchMissionProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const overview = useQuery({ queryKey: qk.myOverview, queryFn: fetchMyOverview, enabled: open, staleTime: 10_000 });
  const openLaunch = useCallback(() => setOpen(true), []);
  const value = useMemo(() => ({ openLaunch }), [openLaunch]);
  return (
    <LaunchMissionContext.Provider value={value}>
      {children}
      <LaunchMissionDialog
        open={open}
        onClose={() => setOpen(false)}
        planets={overview.data?.planets ?? []}
        loading={!overview.data && !overview.isError}
        onLaunched={(issue, planet) => navigate(workspacePath(planet.workspace.slug, `/issues/${issue.id}`))}
      />
    </LaunchMissionContext.Provider>
  );
}
